import moment from 'moment';
import { hairdresserService } from '../../../../_services/hairdresser.service';
import { salonService } from '../../../../_services/salon.service';
import { leaveService } from '../../../../_services/leave.service';
import { paymentService } from '../../../../_services/payment.service';
import { hairdresserActions } from '../../../../_actions/hairdresser.actions';
import siteSetting from '../../../../config/env';

/**
 * The team, their hours and the salon's opening hours, as "Set up with
 * Bailey" sees them. Chapter 3 is built on this, and the read helpers are
 * safe for the ready check to reuse.
 *
 * Every call goes through the same services the Team, Schedules and
 * Settings pages use, so what setup saves is ordinary data the owner can
 * change later. The adapters below never trust HTTP 200 on its own: the v2
 * API answers most refusals with 200 and statusCode 0, and a refusal must
 * never be shown as saved.
 *
 * Two of these writes are not idempotent and carry no idempotency key:
 * adding a person (a login email, and for self-employed a paid seat) and
 * adding leave. The chapter records its intent before each one and checks
 * what exists before sending again. Everything else here is a full replace
 * and safe to repeat.
 */

/* ---------------- small things ---------------- */

/** One self-employed seat, billed by Beu in pounds (config, not the API). */
export const SEAT_PRICE = (siteSetting.stripe && siteSetting.stripe.oneSelfEmpPrice) || 5;

// Leave uses the diary's default leave colour, so it looks the same as
// leave booked from the diary.
const LEAVE_COLOUR = '#E85D75';
// The rota editor offers five-minute steps up to 23:55; there is no 24:00.
export const LATEST_FINISH = 23 * 60 + 55;

const READ_WAIT = 20000;
const WRITE_WAIT = 25000;
const NETWORK = 'I couldn’t reach Beu just now. Check your connection and try again.';
const SLOW = 'That’s taking longer than it should. Check your connection and try again.';

export const me = () => {
  try { return String(localStorage.getItem('userId') || ''); } catch (e) { return ''; }
};
const salonId = () => {
  try { return localStorage.getItem('salonId') || ''; } catch (e) { return ''; }
};

/** Emails are matched trimmed and lower-cased, everywhere. */
export const emailKey = (email) => String(email || '').trim().toLowerCase();
export const validEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || '').trim());
export const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');

/** The reason the server gave, in either envelope, or the fallback. */
export function reasonOf(res, fallback) {
  const d = (res && res.data) || {};
  const e = d.error || {};
  return text(e.responseMessage) || text(e.message) || text(d.messages) || text(d.message) || fallback;
}

// v2 success: statusCode 1. The rota calls are adapted to errorCode 0.
const okV2 = (res) => !!(res && res.data && (res.data.statusCode === 1 || res.data.errorCode === 0) && !res.data.error);
const okStrict = (res) => !!(res && res.data && res.data.statusCode === 1 && !res.data.error && !res.data.errorCode);

function within(promise, ms) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => { const e = new Error(SLOW); e.timeout = true; reject(e); }, ms);
    promise.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

/**
 * Runs one request. Throws an Error whose message can be shown as it is.
 *   err.refused    the server answered and said no (nothing was changed)
 *   err.uncertain  no clear answer (network, timeout, a 5xx): the request
 *                  may still have landed, so look before sending again
 */
async function run(fn, fallback, isOk = okV2, wait = WRITE_WAIT) {
  let res;
  try {
    res = await within(Promise.resolve().then(fn), wait);
  } catch (e) {
    const r = e && e.response;
    const err = new Error(e && e.timeout ? SLOW : r ? reasonOf(r, fallback) : NETWORK);
    err.uncertain = true;
    throw err;
  }
  if (!isOk(res)) {
    const err = new Error(reasonOf(res, fallback));
    err.refused = true;
    throw err;
  }
  return res;
}

/* ---------------- a week of hours ---------------- */

export const DAYS = [
  { key: 'mon', name: 'Monday', short: 'Mon' },
  { key: 'tue', name: 'Tuesday', short: 'Tue' },
  { key: 'wed', name: 'Wednesday', short: 'Wed' },
  { key: 'thu', name: 'Thursday', short: 'Thu' },
  { key: 'fri', name: 'Friday', short: 'Fri' },
  { key: 'sat', name: 'Saturday', short: 'Sat' },
  { key: 'sun', name: 'Sunday', short: 'Sun' },
];

const pad = (n) => String(n).padStart(2, '0');
/** '09:30' to 570. NaN when it isn't a time. */
export const toMin = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || '').trim());
  if (!m) return NaN;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : NaN;
};
export const toHHMM = (min) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
const hhmmOr = (v, fallback) => (Number.isFinite(toMin(v)) ? toHHMM(toMin(v)) : fallback);
/** '9:00' reads as 09:00 on screen, the way the diary shows times. */
export const niceTime = (hhmm) => {
  const m = toMin(hhmm);
  return Number.isFinite(m) ? toHHMM(m) : '';
};

const day = (on = false, start = '09:00', end = '17:00', breaks = []) => ({ on, start, end, breaks });

/**
 * A week, the way both editors hold it:
 *   { mon: { on, start: 'HH:MM', end: 'HH:MM', breaks: [{ start, end }] }, ... }
 * Every day is always present, so the editors never guard.
 */
export function normWeek(w) {
  const src = w || {};
  return DAYS.reduce((out, d) => {
    const x = src[d.key] || {};
    const breaks = Array.isArray(x.breaks) ? x.breaks.filter(Boolean).map((b) => ({ start: b.start || '', end: b.end || '' })) : [];
    return { ...out, [d.key]: day(!!x.on, x.start || '09:00', x.end || '17:00', breaks) };
  }, {});
}

/** Monday to Saturday, 9 to 5: the week to start from when nothing is saved. */
export const defaultWeek = () => DAYS.reduce((w, d) => ({ ...w, [d.key]: day(d.key !== 'sun') }), {});

/** A rota's profiles (minutes, missing day = not working) as a week. */
export function weekFromProfiles(profiles) {
  const list = Array.isArray(profiles) ? profiles.filter(Boolean) : [];
  return DAYS.reduce((w, d) => {
    const p = list.find((x) => x.day === d.key);
    const s = p ? Number(p.startTime) : NaN;
    const e = p ? Number(p.endTime) : NaN;
    if (!Number.isFinite(s) || !Number.isFinite(e)) return { ...w, [d.key]: day(false) };
    // Breaks saved with empty times are skipped, as the rota editor does.
    const breaks = (Array.isArray(p.breakTimes) ? p.breakTimes : [])
      .filter((b) => b && b.startTime !== '' && b.endTime !== '' && Number.isFinite(Number(b.startTime)) && Number.isFinite(Number(b.endTime)))
      .map((b) => ({ start: toHHMM(Number(b.startTime)), end: toHHMM(Number(b.endTime)) }));
    return { ...w, [d.key]: day(true, toHHMM(s), toHHMM(e), breaks) };
  }, {});
}

/** A week as rota profiles. Days off are left out, which is how the rota says "off". */
export function profilesFromWeek(week) {
  const w = normWeek(week);
  return DAYS.filter((d) => w[d.key].on).map((d) => ({
    day: d.key,
    startTime: toMin(w[d.key].start),
    endTime: toMin(w[d.key].end),
    breakTimes: w[d.key].breaks.map((b) => ({ startTime: toMin(b.start), endTime: toMin(b.end) })),
  }));
}

const closed = (r) => r.isClose === true || r.isClose === 'true' || r.isClose === 1;

/** The salon's opening_hours rows ({ dayName, sTime, eTime, isClose }) as a week. */
export function weekFromOpening(rows) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  return DAYS.reduce((w, d) => {
    const r = list.find((x) => String(x.dayName || '').trim().toLowerCase() === d.name.toLowerCase());
    if (!r) return { ...w, [d.key]: day(false) };
    return { ...w, [d.key]: day(!closed(r), hhmmOr(r.sTime, '09:00'), hhmmOr(r.eTime, '17:00')) };
  }, {});
}

/** A week as all seven opening_hours rows, full English day names, as the public page reads them. */
export function openingFromWeek(week) {
  const w = normWeek(week);
  return DAYS.map((d) => ({ dayName: d.name, sTime: hhmmOr(w[d.key].start, '09:00'), eTime: hhmmOr(w[d.key].end, '17:00'), isClose: !w[d.key].on }));
}

/** The same week without breaks (opening hours have none). */
export const withoutBreaks = (week) => {
  const w = normWeek(week);
  return DAYS.reduce((o, d) => ({ ...o, [d.key]: { ...w[d.key], breaks: [] } }), {});
};

/** Keeps the breaks a person already has, wherever they still fit inside the new hours. */
export function keepBreaks(week, from) {
  const w = normWeek(week);
  if (!from) return w;
  const f = normWeek(from);
  return DAYS.reduce((o, d) => {
    const x = w[d.key];
    if (!x.on || !f[d.key].on) return { ...o, [d.key]: x };
    const s = toMin(x.start);
    const e = toMin(x.end);
    const fit = f[d.key].breaks.filter((b) => toMin(b.start) > s && toMin(b.end) < e && toMin(b.end) > toMin(b.start));
    return { ...o, [d.key]: { ...x, breaks: fit.map((b) => ({ ...b })) } };
  }, {});
}

/** A fingerprint for "has this week changed". */
export const weekKey = (week, withBreaks = true) => {
  const w = normWeek(week);
  return JSON.stringify(DAYS.map((d) => {
    const x = w[d.key];
    if (!x.on) return 0;
    return [toMin(x.start), toMin(x.end), withBreaks ? x.breaks.map((b) => [toMin(b.start), toMin(b.end)]) : []];
  }));
};

/** Times for a day that has just been switched on: the week's usual hours, or 9 to 5. */
export function usualTimes(week) {
  const w = normWeek(week);
  const counts = {};
  DAYS.forEach((d) => {
    if (!w[d.key].on) return;
    const k = `${w[d.key].start}-${w[d.key].end}`;
    counts[k] = (counts[k] || 0) + 1;
  });
  const best = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  if (!best) return { start: '09:00', end: '17:00' };
  const [start, end] = best.split('-');
  return { start, end };
}

/** Every other working day takes this day's hours and breaks. Days off stay off. */
export function copyDay(week, fromKey) {
  const w = normWeek(week);
  const src = w[fromKey];
  if (!src || !src.on) return w;
  return DAYS.reduce((o, d) => {
    const x = w[d.key];
    if (d.key === fromKey || !x.on) return { ...o, [d.key]: x };
    return { ...o, [d.key]: { on: true, start: src.start, end: src.end, breaks: src.breaks.map((b) => ({ ...b })) } };
  }, {});
}

/**
 * What can't be saved, by day: { tue: 'The finish needs to be after the start.' }.
 * The API checks none of this, so it is checked here before anything is sent.
 */
export function weekProblems(week, { breaks = true } = {}) {
  const w = normWeek(week);
  const out = {};
  DAYS.forEach((d) => {
    const x = w[d.key];
    if (!x.on) return;
    const s = toMin(x.start);
    const e = toMin(x.end);
    if (!Number.isFinite(s) || !Number.isFinite(e)) { out[d.key] = 'Add a start and a finish time.'; return; }
    if (e <= s) { out[d.key] = 'The finish needs to be after the start.'; return; }
    if (e > LATEST_FINISH) { out[d.key] = 'The latest finish is 23:55.'; return; }
    if (!breaks) return;
    const bad = x.breaks.map((b) => {
      const bs = toMin(b.start);
      const be = toMin(b.end);
      if (!Number.isFinite(bs) || !Number.isFinite(be)) return 'Add both break times, or remove the break.';
      if (be <= bs) return 'The break needs to end after it starts.';
      if (bs <= s || be >= e) return 'The break needs to sit inside the working hours.';
      return '';
    }).find(Boolean);
    if (bad) out[d.key] = bad;
  });
  return out;
}

const listPhrase = (parts) => (parts.length <= 1 ? parts[0] || '' : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

/** "Tue to Sat", "Mon, Wed and Fri", "Every day". */
export function daysPhrase(week) {
  const w = normWeek(week);
  const on = DAYS.map((d, i) => (w[d.key].on ? i : -1)).filter((i) => i >= 0);
  if (!on.length) return '';
  if (on.length === 7) return 'Every day';
  const runs = [];
  on.forEach((i) => {
    const last = runs[runs.length - 1];
    if (last && last[last.length - 1] === i - 1) last.push(i); else runs.push([i]);
  });
  const parts = [];
  runs.forEach((r) => {
    if (r.length >= 3) parts.push(`${DAYS[r[0]].short} to ${DAYS[r[r.length - 1]].short}`);
    else r.forEach((i) => parts.push(DAYS[i].short));
  });
  return listPhrase(parts);
}

/** "Tue to Sat, 9:00 to 17:00", "Mon to Fri, hours vary" or "Not working". */
export function describeWeek(week, offWord = 'Not working') {
  const w = normWeek(week);
  const on = DAYS.filter((d) => w[d.key].on);
  if (!on.length) return offWord;
  const starts = new Set(on.map((d) => w[d.key].start));
  const ends = new Set(on.map((d) => w[d.key].end));
  const times = starts.size === 1 && ends.size === 1
    ? `${niceTime(w[on[0].key].start)} to ${niceTime(w[on[0].key].end)}`
    : 'hours vary';
  return `${daysPhrase(w)}, ${times}`;
}

/* ---------------- the team ---------------- */

/**
 * Whether a rota changes from week to week. The list sends only the weeks
 * that have working days, keyed by weekNumber and in no set order, so
 * anything other than a single week 1 is a multi-week rota. (A two-week rota
 * whose second week is all days off can't be told apart from a one-week
 * rota here: the list leaves the empty week out.)
 */
export function isMultiWeek(schedule) {
  const weeks = Array.isArray(schedule) ? schedule.filter(Boolean) : [];
  if (weeks.length > 1) return true;
  return weeks.some((w) => (w.weekNumber !== undefined && Number(w.weekNumber) !== 1)
    || (Array.isArray(w.profiles) && w.profiles.some((p) => p && p.weekNumber !== undefined && Number(p.weekNumber) !== 1)));
}

/**
 * Week 1 of a rota's schedule rather than whichever week the list put first.
 * A multi-week rota with nothing in week 1 falls back to the first week
 * listed, which is only ever shown, never saved.
 */
export function firstWeekOf(schedule) {
  const weeks = Array.isArray(schedule) ? schedule.filter(Boolean) : [];
  return weeks.find((w) => Number(w.weekNumber) === 1) || weeks[0] || null;
}

/**
 * One person, from a staffRotaV2 row:
 *   { id, name, first, email, photo, owner, type: 1 employed | 2 self-employed,
 *     cut (the salon's cut, %), jobTitle, rotaStart, week (null = no rota yet),
 *     multiWeek (their rota changes week to week, so setup never writes it) }
 * An empty rota counts as no rota: there is nothing on it to overwrite.
 */
export function personFrom(row) {
  const h = row.hairdresserInfo || {};
  const rota = (row.rotaInfo && row.rotaInfo.rota) || null;
  const schedule = rota && Array.isArray(rota.schedule) ? rota.schedule : [];
  const sched = firstWeekOf(schedule);
  const profiles = sched && Array.isArray(sched.profiles) ? sched.profiles.filter(Boolean) : [];
  const name = String(row.name || '').trim();
  const cut = Number(h.salonCommissionRate);
  return {
    id: String(row._id),
    name: name || 'Team member',
    first: firstName(name) || 'Them',
    email: String(row.email || '').trim(),
    photo: (row.userImage && row.userImage.circleImage) || '',
    owner: Number(h.isSalonOwner) === 1,
    type: Number(h.employmentTypeId) === 2 ? 2 : 1,
    cut: Number.isFinite(cut) && cut > 0 ? cut : 0,
    jobTitle: String(h.jobTitle || '').trim(),
    rotaStart: (rota && rota.startDate) || null,
    week: profiles.length ? weekFromProfiles(profiles) : null,
    multiWeek: isMultiWeek(schedule),
  };
}

/** A self-employed stylist who isn't the signed-in account: they set their own hours and time off. */
export const setsOwnHours = (p, isMe) => !!p && p.type === 2 && !p.owner && !(isMe && isMe(p));

/**
 * Everyone at this location, straight from the service (never the redux
 * action, whose failure would put the diary and Team into their error
 * state). Placeholder rows ('Unknown') and removed people are dropped.
 */
export async function loadTeam() {
  const res = await run(
    () => hairdresserService.staffRotaV2({ hairdresserId: me(), searchKey: '', page: 1, listType: 1, isGetRota: 1 }),
    'I couldn’t load your team just now.',
    okV2,
    READ_WAIT,
  );
  const result = res.data.responseData && res.data.responseData.result;
  return (Array.isArray(result) ? result : [])
    .filter((r) => r && r._id && r.name !== 'Unknown' && r.email !== 'Unknown')
    .map(personFrom);
}

/** After any team, rota or cut write: Team, the diary and checkout read this store. */
export function refreshTeamStore(dispatch) {
  if (!dispatch) return;
  try {
    const p = dispatch(hairdresserActions.hairdresserStaffRota({ isGetRota: 1, hairdresserId: me() }));
    if (p && typeof p.catch === 'function') p.catch(() => { /* the Team page reloads on its own */ });
  } catch (e) { /* the Team page reloads on its own */ }
}

/**
 * A phone number as E.164, the way EditStaff sends it. The API stores
 * digits without the plus; UK numbers are the common case.
 */
export function toE164(raw) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s) return '';
  if (s[0] === '+') return `+${s.slice(1).replace(/\D/g, '')}`;
  const digits = s.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) return `+${digits.slice(2)}`;
  if (digits[0] === '0') return `+44${digits.slice(1)}`;
  if (digits.length >= 11) return `+${digits}`;
  return `+44${digits}`;
}
export const validPhone = (raw) => {
  const n = toE164(raw).replace(/\D/g, '').length;
  return n >= 9 && n <= 15;
};

/** The owner's currency for a new person's record, never pounds by default for an Irish salon. */
export function currencyOf(user) {
  const h = (user && user.userInfo && user.userInfo.hairdresser) || {};
  return { currency: h.currency || 'GBP', symbol: h.currencySymbol || '£', country: h.currencyCountry || 'GB' };
}

/**
 * Adds one person. This single call creates their login, emails it to them
 * and, for self-employed, adds a paid seat to the plan. There is no way to
 * add someone quietly, and no resend. Never call it twice for one email:
 * the chapter records the intent first and checks the team list before
 * every send.
 *
 * The server refuses an add without a mobile number (addStaff's validator),
 * so a missing or unusable one is refused here and never sent.
 *
 * People found in the files are added exactly like anyone else. The import
 * doesn't make staff records (staff are out of the import on this server,
 * and addStaff ignores an importTempId), so there's nothing to link to.
 */
export async function addPerson({ name, email, phone, type, jobTitle, currency }) {
  if (!validPhone(phone)) {
    const e = new Error('Add their mobile number. Beu needs one to add them.');
    e.refused = true;
    throw e;
  }
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  const first = parts[0] || '';
  const last = parts.slice(1).join(' ');
  const cur = currency || { currency: 'GBP', symbol: '£', country: 'GB' };
  const fd = new FormData();
  fd.append('name', `${first} ${last}`.trim());
  fd.append('first_name', first);
  fd.append('last_name', last);
  fd.append('email', String(email || '').trim());
  fd.append('phoneNumber', toE164(phone));
  fd.append('jobTitle', jobTitle || 'Stylist');
  fd.append('hairdresserBookingInterval', 15);
  fd.append('shortBio', '');
  fd.append('employmentTypeId', type === 2 ? 2 : 1);
  fd.append('salonId', salonId());
  fd.append('countryId', '217');
  fd.append('currency', cur.currency);
  fd.append('currencySymbol', cur.symbol);
  fd.append('currencyCountry', cur.country);
  // 0% unless the owner sets it, never pre-filled (Ben's decision).
  fd.append('salonCommissionRate', 0);
  fd.append('productCommissionRate', 0);
  fd.append('voucherCommissionRate', 0);
  fd.append('activationCode', '');
  await run(() => hairdresserService.addStaff(fd), 'I couldn’t add them just now.', okStrict);
}

/* ---------------- the salon's cut ---------------- */

/** 0 to 100, up to two decimals. An empty box means 0. null when it can't be a cut. */
export function parseRate(v) {
  const s = String(v == null ? '' : v).trim().replace(/%$/, '').trim();
  if (s === '') return 0;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(s)) return null;
  const n = Number(s);
  return n >= 0 && n <= 100 ? n : null;
}
const round2 = (n) => Math.round(n * 100) / 100;

/** The plain meaning, next to the number. */
export function cutMeaning(rate) {
  if (rate === null || rate === undefined) return 'Use a number from 0 to 100.';
  if (rate === 0) return 'They keep all of their takings, before any card fees they pay.';
  if (rate === 100) return 'The salon gets all of their takings.';
  return `The salon gets ${rate}% of each booking. They keep ${round2(100 - rate)}%, before any card fees they pay.`;
}
/** "Salon’s cut: 0%. They keep all of their takings, before any card fees." */
export function cutLine(rate) {
  const r = Number(rate) || 0;
  if (!r) return 'Salon’s cut: 0%. They keep all of their takings, before any card fees.';
  return `Salon’s cut: ${r}%. They keep ${round2(100 - r)}%, before any card fees.`;
}

const present = (v) => v !== undefined && v !== null && v !== '';
const plainError = (message) => { const e = new Error(message); e.refused = true; e.plain = true; return e; };

/**
 * editStaff's form for one person, built from their edit-info record, with
 * only `change` (field to value) as the difference.
 *
 * editStaff is not a partial update. It always writes name, first_name,
 * last_name, email and phoneNumber to the login, and employmentTypeId and
 * hairdresserBookingInterval to their record at this site, and its
 * validator insists on jobTitle and salonCommissionRate too. Those go back
 * exactly as stored. The phone number goes back as the digits the account
 * holds (it's stored as a number, so that's lossless; toE164 would rewrite
 * an old local number as +44). The employment type goes back as stored,
 * because a changed one moves a paid seat.
 *
 * Everything else editStaff only writes when it's sent (product and voucher
 * rates, currency, bio, slot packing, online booking, photos; countryId
 * isn't read at all), so it's left out unless it's the change. Throws a
 * plain message when the record lacks something the validator needs, so
 * nothing half-made is sent.
 */
export function staffEditForm(staffId, u, h, change = {}, { self = false } = {}) {
  const who = self ? 'your profile' : 'their record';
  const whose = self ? 'Your profile' : 'Their record';
  const first = String(present(u.first_name) ? u.first_name : String(u.name || '').trim().split(/\s+/)[0] || '').trim();
  const name = String(present(u.name) ? u.name : `${first} ${u.last_name || ''}`).trim();
  const email = String(u.email || '').trim();
  const phone = present(u.phoneNumber) && Number(u.phoneNumber) !== 0 ? String(u.phoneNumber).trim() : '';
  const jobTitle = present(change.jobTitle) ? String(change.jobTitle) : String(h.jobTitle || '').trim();
  const rate = present(change.salonCommissionRate) ? change.salonCommissionRate : h.salonCommissionRate;
  if (!first || !name || !email || !present(h.employmentTypeId) || !present(rate)) throw plainError(`I couldn’t read ${who} safely, so nothing was changed.`);
  if (!phone) throw plainError(`${whose} has no mobile number yet, and Beu needs one to save this. Add it on Team, then try again.`);
  if (!jobTitle) throw plainError(`${whose} has no job title yet, and Beu needs one to save this. Add it on Team, then try again.`);

  const fd = new FormData();
  fd.append('userId', staffId);
  fd.append('name', name);
  fd.append('first_name', first);
  fd.append('last_name', present(u.last_name) ? String(u.last_name) : '');
  fd.append('email', email);
  fd.append('phoneNumber', phone);
  fd.append('jobTitle', jobTitle);
  fd.append('employmentTypeId', h.employmentTypeId);
  fd.append('hairdresserBookingInterval', present(h.hairdresserBookingInterval) ? h.hairdresserBookingInterval : 0);
  fd.append('salonId', salonId());
  // The validator needs it. Without the team permission the server drops
  // it; otherwise it goes back as stored unless it's the change.
  fd.append('salonCommissionRate', rate);
  Object.keys(change).forEach((k) => {
    if (k === 'jobTitle' || k === 'salonCommissionRate' || change[k] === undefined) return;
    fd.append(k, change[k]);
  });
  return fd;
}

/** One person's edit-info record, { u, h }. Throws a message that can be shown. */
export async function readStaffRecord(staffId, fallback = 'I couldn’t open their record just now.') {
  let res;
  try {
    res = await within(Promise.resolve().then(() => hairdresserService.getStaffEditInfoV2({ hairdresserId: staffId })), READ_WAIT);
  } catch (e) {
    const r = e && e.response;
    throw new Error(e && e.timeout ? SLOW : r ? reasonOf(r, fallback) : NETWORK);
  }
  const d = (res && res.data) || {};
  const payload = d.responseData || d.data || null;
  const u = payload && payload.userInfo;
  const h = u && u.hairdresser;
  if (!h || d.statusCode === 0) throw new Error(reasonOf(res, fallback));
  return { u, h };
}

/**
 * Sets one self-employed stylist's salon cut. Their record is read first
 * and only the cut changes (see staffEditForm): product and voucher rates,
 * photos, currency and the rest are never sent, so they can't be touched.
 * Safe to repeat.
 */
export async function saveCut(staffId, rate) {
  const { u, h } = await readStaffRecord(staffId);
  if (Number(h.employmentTypeId) !== 2) throw new Error('Only self-employed stylists pay the salon a cut.');
  const fd = staffEditForm(staffId, u, h, { salonCommissionRate: rate });
  await run(() => hairdresserService.editStaff(fd), 'I couldn’t save their cut just now.', okStrict);
}

/* ---------------- opening hours ---------------- */

/**
 * The saved opening hours: the seven rows, or [] when they were never
 * saved (Settings then shows a made-up week that isn't on the server).
 * Throws when they can't be read, so a proposal never overwrites real hours.
 */
export async function loadOpeningHours() {
  let res;
  try {
    res = await within(Promise.resolve().then(() => salonService.getOpeningHours(salonId())), READ_WAIT);
  } catch (e) {
    const r = e && e.response;
    throw new Error(e && e.timeout ? SLOW : r ? reasonOf(r, 'I couldn’t read your opening hours.') : NETWORK);
  }
  const d = (res && res.data) || {};
  const rd = d.responseData || {};
  const hours = (rd.data && rd.data.opening_hours) || (d.data && d.data.opening_hours) || rd.opening_hours;
  if (Array.isArray(hours)) return hours.filter(Boolean);
  if (d.statusCode === 1) return [];
  throw new Error(reasonOf(res, 'I couldn’t read your opening hours.'));
}

/** Saves all seven days (the call replaces the whole week). Safe to repeat. */
export async function saveOpeningHours(week) {
  const rows = openingFromWeek(week);
  await run(() => salonService.updateOpeningHours(salonId(), rows), 'I couldn’t save your opening hours.', okStrict);
  return rows;
}

/* ---------------- each person's rota ---------------- */

/**
 * Saves one person's repeating week at this location: a full replace of
 * their rota with a one-week pattern, so safe to repeat. Never for a rota
 * with more than one week, which it would cut down to week 1. The rota's existing start date is sent back as it is;
 * only a brand-new rota starts this Monday (never a future date for
 * someone already working, which would make Staff Pay miss days).
 */
export async function saveWeek(personId, week, rotaStart, multiWeek = false) {
  // Only week 1 is ever sent, and a save replaces the whole rota, so a rota
  // that changes week to week is never written from setup.
  if (multiWeek) throw plainError('This rota changes from week to week, so change it in Team, under Schedules.');
  const known = rotaStart ? moment(rotaStart, [moment.ISO_8601, 'YYYY-MM-DD']) : null;
  const start = known && known.isValid() ? known.format('YYYY-MM-DD') : moment().startOf('isoWeek').format('YYYY-MM-DD');
  await run(
    () => hairdresserService.saveRota({ hairdresserId: personId, rotaStartDate: start, rota: [{ weekNumber: 1, profiles: profilesFromWeek(week) }] }),
    'I couldn’t save these hours.',
  );
}

/* ---------------- holidays and closures ---------------- */

export const todayISO = () => moment().format('YYYY-MM-DD');
const dayOf = (v) => {
  if (!v) return '';
  const m = moment(v, [moment.ISO_8601, 'YYYY-MM-DD']);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

/** "24 Dec", "24 to 27 Dec", "30 Dec to 2 Jan 2027". */
export function rangeLabel(start, end) {
  const a = moment(start, 'YYYY-MM-DD');
  const b = moment(end || start, 'YYYY-MM-DD');
  if (!a.isValid()) return '';
  const year = moment().year();
  const full = (m) => `${m.format('D MMM')}${m.year() !== year ? ` ${m.year()}` : ''}`;
  if (!b.isValid() || b.isSame(a, 'day')) return full(a);
  if (a.isSame(b, 'month')) return `${a.format('D')} to ${full(b)}`;
  return `${full(a)} to ${full(b)}`;
}

/** Records are keyed by person and dates, so a retry never books the same leave twice. */
export const leaveKey = (personId, start, end) => `${personId}|${start}|${end}`;

/**
 * One person's leave: [{ id, start, end, title, leaveType }], or null when
 * the list can't be read. The list's shape isn't used anywhere else in the
 * app yet, so it's read defensively and null means "don't know".
 */
export async function loadLeave(personId) {
  let res;
  try {
    res = await within(Promise.resolve().then(() => leaveService.getLeaves({ hairdresserId: personId })), READ_WAIT);
  } catch (e) {
    return null;
  }
  const d = (res && res.data) || {};
  if (d.statusCode === 0 || d.errorCode || d.error) return null;
  const rd = d.responseData !== undefined ? d.responseData : d.data;
  const list = Array.isArray(rd) ? rd : rd && (rd.result || rd.holidays || rd.holidayList || rd.leaves || rd.list || rd.data);
  if (!Array.isArray(list)) return null;
  return list.filter(Boolean).map((x) => {
    const h = x.holidayInfo || x;
    const start = dayOf(h.startDate || h.holidayStartDate);
    if (!start) return null;
    return {
      id: String(h._id || ''),
      start,
      end: dayOf(h.endDate || h.holidayEndDate) || start,
      title: text(h.title),
      leaveType: text(h.leaveType),
    };
  }).filter(Boolean);
}

/**
 * Books whole days off for one person, both dates included. Not idempotent:
 * every call makes a new record, so the chapter keys what it made by person
 * and dates. It doesn't move or cancel bookings already on those days, and
 * nothing is sent to anyone.
 */
export async function addLeave({ personId, start, end, title, leaveType }) {
  await run(
    () => leaveService.createLeave({
      hairdresserId: personId,
      startDate: start,
      endDate: end || start,
      title: String(title || '').slice(0, 200),
      diaryColour: LEAVE_COLOUR,
      emoji: null,
      leaveType: leaveType || 'holiday',
    }),
    'I couldn’t add that just now.',
    (r) => { const d = (r && r.data) || {}; return !!r && !(d.errorCode || d.error || d.statusCode === 0); },
  );
}

/* ---------------- the plan ---------------- */

/**
 * Monthly or yearly, and how many self-employed seats are already paid
 * for. null when it can't be read; the chapter then just leaves the plan
 * line out. There is no yearly seat price in the app's config, so the
 * button always shows the monthly one.
 */
export async function loadPlan() {
  try {
    const r = await within(Promise.resolve().then(() => paymentService.subscriptionGet({})), READ_WAIT);
    const d = (r && r.data) || {};
    if (d.statusCode !== 1) return null;
    const rd = d.responseData || {};
    const interval = rd.result && rd.result.plan && rd.result.plan.interval === 'year' ? 'year' : 'month';
    const seats = Number(rd.selfEmpPlan && rd.selfEmpPlan.quantity) || 0;
    return { interval, seats };
  } catch (e) {
    return null;
  }
}
